import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePatient } from '../../hooks/usePatient';
import { useMessagesThread, useMessageConfirmedStatus, useSendMessage } from '../../hooks/api';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { PageDataBoundary } from '../../components/PageDataBoundary/PageDataBoundary';
import { PatientSwitcher } from '../../components/PatientSwitcher';
import { EmptyState, SendSuccessDialog } from '../../components';
import { ModeTabs } from './components/ModeTabs';
import type { MessageMode } from './components/ModeTabs';
import { ComposeView } from './components/ComposeView';
import { HistoryView } from './components/HistoryView';
import type { ReplyTo } from '../../lib/api';
import { isMessageSendingClosed } from '../../utils';
import {
  Container,
  ContentArea,
} from './MessagePage.styled';

const getModeFromHash = (hash: string): MessageMode | null => {
  if (hash === '#history') return 'history';
  if (hash === '#compose') return 'compose';
  return null;
};

interface LocationState {
  replyTo?: ReplyTo;
  initialMode?: MessageMode;
}

export const MessagePage: React.FC = () => {
  const {
    selectedPatientId,
    selectedPatientIndex,
    linkedPatients,
    selectPatientByIndex,
    selectedPatient,
  } = usePatient();

  // Location state for reply and initial mode
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const initialReplyTo = locationState?.replyTo;

  const modeFromHash = getModeFromHash(location.hash);
  const initialMode = modeFromHash || locationState?.initialMode;

  const isSendingClosed = isMessageSendingClosed(selectedPatient);

  const [activeMode, setActiveMode] = useState<MessageMode>(
    isSendingClosed ? 'history' : initialMode || 'compose'
  );
  const [messageText, setMessageText] = useState('');
  const [hideFromPatient, setHideFromPatient] = useState(false);
  const [showSendSuccessDialog, setShowSendSuccessDialog] = useState(false);
  const [replyTo, setReplyTo] = useState<ReplyTo | null>(initialReplyTo || null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // キーボード表示状態をbody data属性に同期（BottomNavigation非表示用）
  useEffect(() => {
    if (isInputFocused) {
      document.body.dataset.keyboardOpen = 'true';
    } else {
      delete document.body.dataset.keyboardOpen;
    }
    return () => {
      delete document.body.dataset.keyboardOpen;
    };
  }, [isInputFocused]);

  const handleModeChange = useCallback((newMode: MessageMode) => {
    setActiveMode(newMode);
    navigate(`${location.pathname}${location.search}#${newMode}`, { replace: true });
  }, [navigate, location.pathname, location.search]);

  useEffect(() => {
    // state経由のinitialModeをハッシュに同期
    if (!modeFromHash && initialMode) {
      navigate(`${location.pathname}${location.search}#${initialMode}`, {
        replace: true,
      });
    }
    // replyTo stateの消去（既存ロジック維持）
    if (initialReplyTo) {
      window.history.replaceState({}, document.title);
    }
  }, []);

  const isSelf = selectedPatient?.relationship === '本人';

  // 送信閉鎖中に compose タブまたは #compose 直リンクで来た場合は history へ正規化
  useEffect(() => {
    if (isSendingClosed && (activeMode === 'compose' || location.hash === '#compose')) {
      if (activeMode !== 'history') {
        setActiveMode('history');
      }
      if (location.hash !== '#history') {
        navigate(`${location.pathname}${location.search}#history`, { replace: true });
      }
    }
  }, [isSendingClosed, activeMode, navigate, location.pathname, location.search, location.hash]);

  // 利用者変更時にリプライとメッセージをクリア
  const prevPatientIdRef = useRef<number | null>(selectedPatientId);
  useEffect(() => {
    if (prevPatientIdRef.current !== null && prevPatientIdRef.current !== selectedPatientId) {
      setReplyTo(null);
      setMessageText('');
      setShowSendSuccessDialog(false);
    }
    prevPatientIdRef.current = selectedPatientId;
  }, [selectedPatientId]);

  // 本人の場合はhideFromPatientを常にfalseにリセット
  useEffect(() => {
    if (isSelf) {
      setHideFromPatient(false);
    }
  }, [selectedPatientId, isSelf]);

  // API hooks — threads 付きの全データを一括取得
  const {
    data: threadData,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useMessagesThread(selectedPatientId);

  // 確認済みステータスの軽量ポーリング（15秒間隔）
  useMessageConfirmedStatus(selectedPatientId);

  const sendMessageMutation = useSendMessage();

  // Speech recognition hook
  const {
    state: voiceState,
    lastSegment,
    segmentId,
    startRecording,
    stopRecording,
    resetRecording,
  } = useSpeechRecognition();

  const isRecording = voiceState === 'recording';
  const isProcessing = voiceState === 'processing';

  // 音声入力結果をmessageTextに反映（差分のみ追記）
  useEffect(() => {
    if (lastSegment) {
      setMessageText((prev) => prev + lastSegment);
    }
  }, [segmentId]);

  // スレッドIDを取得（送信用: 最初のスレッドを使用）
  const selectedThreadId = threadData?.threads?.[0]?.thread_id || null;

  // Handlers
  const handleSend = useCallback(() => {
    if (!selectedPatientId || !messageText.trim()) return;

    sendMessageMutation.mutate(
      {
        patient_id: selectedPatientId,
        content: messageText.trim(),
        reply_to: replyTo || undefined,
        thread_id: undefined,
        hide_from_patient: hideFromPatient || undefined,
      },
      {
        onSuccess: () => setShowSendSuccessDialog(true),
      }
    );

    setMessageText('');
    setReplyTo(null);
    setHideFromPatient(false);
  }, [selectedPatientId, messageText, selectedThreadId, sendMessageMutation, replyTo]);

  const handleVoiceInput = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      resetRecording();
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording, resetRecording]);

  const handlePatientChange = useCallback(
    (index: number) => {
      selectPatientByIndex(index);
    },
    [selectPatientByIndex]
  );

  const officeName = threadData?.office_name || '事業所';

  // 患者データがない場合
  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <EmptyState
        fullPage
        title="連携しているご家族が見つかりません"
        description="事業所と連携を行ってください"
        icon="👤"
      />
    );
  }

  return (
    <PageDataBoundary ready={!isLoadingMessages}>
      <Container>
        {/* 利用者切り替えタブ */}
        {!isInputFocused && linkedPatients && (
          <PatientSwitcher
            patients={linkedPatients}
            selectedIndex={selectedPatientIndex}
            onPatientChange={handlePatientChange}
          />
        )}

        {/* モード切替タブ */}
        {!isInputFocused && (
          <ModeTabs
            activeMode={activeMode}
            onModeChange={handleModeChange}
            hideCompose={isSendingClosed}
          />
        )}

        {/* メインコンテンツ */}
        <ContentArea>
          {activeMode === 'compose' && !isSendingClosed ? (
            <ComposeView
              officeName={officeName}
              messageText={messageText}
              onMessageTextChange={setMessageText}
              hideFromPatient={hideFromPatient}
              onHideFromPatientChange={setHideFromPatient}
              onVoiceInput={handleVoiceInput}
              onSend={handleSend}
              isSending={sendMessageMutation.isPending}
              isRecording={isRecording}
              isProcessing={isProcessing}
              showHideFromPatient={!isSelf}
              onInputFocusChange={setIsInputFocused}
            />
          ) : (
            <HistoryView
              threads={
                isSelf
                  ? (threadData?.threads || []).filter(t => !t.hide_from_patient)
                  : (threadData?.threads || [])
              }
              patientId={selectedPatientId}
              error={messagesError}
            />
          )}
        </ContentArea>

        <SendSuccessDialog
          isOpen={showSendSuccessDialog}
          onClose={() => setShowSendSuccessDialog(false)}
          onConfirm={() => {
            setShowSendSuccessDialog(false);
            handleModeChange('history');
          }}
          officeName={officeName}
        />
      </Container>
    </PageDataBoundary>
  );
};

export default MessagePage;
